import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ICONS } from '../constants';
import { auth, createSupportTicket } from '../services/firebase';

interface SupportModalProps {
  onClose: () => void;
}

export const SupportModal: React.FC<SupportModalProps> = ({ onClose }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) {
        setErrorMsg("You must be logged in to submit a ticket.");
        return;
    }
    
    setIsSubmitting(true);
    setErrorMsg('');

    try {
        await createSupportTicket(
            auth.currentUser.uid,
            auth.currentUser.email || 'No Email',
            subject,
            message
        );
        setIsSuccess(true);
        setTimeout(() => {
            onClose();
        }, 2500);
    } catch (error: any) {
        console.error("Support Ticket Error:", error);
        // Show the actual error message from Firebase
        if (error.code === 'permission-denied') {
             setErrorMsg("Permission Denied. Please ensure Firestore Rules are updated in the Firebase Console.");
        } else {
             setErrorMsg(error.message || "Failed to submit ticket. Please try again.");
        }
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-xl flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/5 bg-zinc-900/50">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-zinc-800 rounded-full text-zinc-400">
                    <ICONS.MessageSquare className="w-5 h-5" />
                </div>
                <h2 className="text-xl font-bold uppercase tracking-widest text-white">
                  Contact Support
                </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full text-zinc-400 hover:text-white transition-colors"
            >
              <ICONS.Close className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 md:p-8">
            {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-8 text-center animate-in fade-in zoom-in">
                    <div className="w-16 h-16 bg-green-900/20 text-green-500 rounded-full flex items-center justify-center mb-4">
                        <ICONS.Check className="w-8 h-8" />
                    </div>
                    <h3 className="text-white text-lg font-bold mb-2">Message Sent</h3>
                    <p className="text-zinc-400 text-sm">We have received your inquiry and will get back to you shortly.</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-xs text-zinc-500 mb-4">
                        Having trouble? Fill out the form below and our team will assist you.
                    </p>

                    {errorMsg && (
                        <div className="p-3 bg-red-900/20 border border-red-500/20 rounded text-red-400 text-xs text-center font-mono break-words">
                            {errorMsg}
                        </div>
                    )}

                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Subject</label>
                        <select 
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            required
                            className="w-full bg-black/40 border border-zinc-800 rounded px-4 py-3 text-sm text-white focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50"
                        >
                            <option value="">Select a Topic...</option>
                            <option value="Billing Issue">Billing & Subscription</option>
                            <option value="Technical Bug">Technical Issue / Bug</option>
                            <option value="Feature Request">Feature Request</option>
                            <option value="Account Help">Account Management</option>
                            <option value="Other">Other Inquiry</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Message</label>
                        <textarea 
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            required
                            rows={5}
                            placeholder="Describe your issue in detail..."
                            className="w-full bg-black/40 border border-zinc-800 rounded px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-white/50 focus:ring-1 focus:ring-white/50 resize-none"
                        />
                    </div>

                    <button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-white text-black font-bold uppercase tracking-[0.15em] py-3 rounded text-xs hover:bg-zinc-200 transition-colors disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
                    >
                        {isSubmitting ? (
                            <>Sending...</>
                        ) : (
                            <>Submit Request <ICONS.ArrowRight className="w-3 h-3" /></>
                        )}
                    </button>
                </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};